import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import api from '../../utils/api'

/**
 * PaymentActionMenu — three-dot menu for a `payments` row.
 *
 * Mirrors the case-detail FinanceTable action menu so the Billing page and
 * the case detail share one source of truth for "Mark as Paid by Stripe /
 * Paid Manually / Unpaid" + payment-link generation.
 *
 * Props:
 *   payment         — { id, rawStatus | status, paymentUrl }
 *   mediatorUserId  — optional; required to generate a Stripe payment link
 *   chequeImageUrl  — optional; when set, adds a "View Cheque" item that
 *                     opens an image preview modal
 *   onToast         — optional ({ type, message }) => void
 *   onChanged       — optional () => void, called after a successful change
 */
const CHEQUE_MAX_BYTES = 5 * 1024 * 1024
const CHEQUE_ACCEPTED_MIME = ['image/jpeg', 'image/png']
const CHEQUE_ACCEPT_ATTR = 'image/jpeg,image/png'

function fileToBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
            const result = reader.result || ''
            const idx = String(result).indexOf(',')
            resolve(idx >= 0 ? String(result).slice(idx + 1) : String(result))
        }
        reader.onerror = reject
        reader.readAsDataURL(file)
    })
}

export default function PaymentActionMenu({ payment, mediatorUserId, chequeImageUrl, onToast, onChanged }) {
    const [open, setOpen] = useState(false)
    const [menuPos, setMenuPos] = useState(null)
    const [busy, setBusy] = useState(false)
    const [chequeOpen, setChequeOpen] = useState(false)
    // Image-load state for the cheque preview modal. Reset every time the
    // modal opens (or the URL changes) so a cached image doesn't keep the
    // previous "loaded" state and a fresh URL gets its own spinner.
    const [chequeLoaded, setChequeLoaded] = useState(false)
    const [chequeError, setChequeError] = useState(false)
    const buttonRef = useRef(null)
    // Hidden file input for the Upload Cheque action.  Clicking the menu
    // item programmatically opens the OS file picker; on file pick we
    // upload, then immediately flip the payment to paid_manually so the
    // status reflects that this invoice was settled by physical cheque.
    const fileInputRef = useRef(null)
    const [uploading, setUploading] = useState(false)

    useEffect(() => {
        if (chequeOpen) {
            setChequeLoaded(false);
            setChequeError(false);
        }
    }, [chequeOpen, chequeImageUrl])

    useEffect(() => {
        if (!open || !buttonRef.current) return
        const rect = buttonRef.current.getBoundingClientRect()
        const menuWidth = 224
        const top = rect.bottom + 4
        const left = Math.max(8, Math.min(window.innerWidth - menuWidth - 8, rect.right - menuWidth))
        setMenuPos({ top, left })
    }, [open])

    const status = (payment.rawStatus || payment.status || '').toLowerCase()
    const isPaid = status === 'paid' || status === 'paid_manually'

    const setStatus = async (newStatus, friendlyName) => {
        try {
            setBusy(true)
            await api.patch(`/cases/payments/${payment.id}/status`, { status: newStatus, source: payment.source })
            onToast?.({ type: 'success', message: `Marked as ${friendlyName}` })
            setOpen(false)
            onChanged?.()
        } catch (err) {
            onToast?.({ type: 'error', message: 'Failed to update payment status' })
        } finally {
            setBusy(false)
        }
    }

    const handleChequeFile = async (file) => {
        if (!file) return
        if (!CHEQUE_ACCEPTED_MIME.includes(file.type)) {
            onToast?.({ type: 'error', message: 'Unsupported file type. Use JPEG or PNG.' })
            return
        }
        if (file.size > CHEQUE_MAX_BYTES) {
            onToast?.({ type: 'error', message: 'File too large. Max 5 MB.' })
            return
        }
        // Track upload success separately so we can still flag the row as
        // changed (cheque is now on file) even if the follow-up status
        // PATCH fails.  Without this the UI keeps showing "Upload Cheque"
        // while the BE already has the image — a confusing split state.
        let uploadSucceeded = false
        try {
            setUploading(true)
            const base64 = await fileToBase64(file)
            await api.post(`/billing/${payment.id}/cheque`, {
                file_name: file.name,
                file_base64: base64,
                content_type: file.type,
                source: payment.source,
            })
            uploadSucceeded = true
            // Per product spec: uploading a cheque image means this invoice
            // was settled by physical cheque — mark it paid manually.  Skip
            // the PATCH if it's already paid (Stripe-paid invoices keep
            // their own status).
            const curr = (payment.rawStatus || payment.status || '').toLowerCase()
            if (curr !== 'paid' && curr !== 'paid_manually') {
                // `source` is required: the backend routes hourly rows to
                // hourly_invoice_payment and everything else to payments. Omit it
                // and an hourly payment is looked up in payments by the same
                // numeric id, which either 404s or silently updates an unrelated
                // row that happens to share it.
                await api.patch(`/cases/payments/${payment.id}/status`, { status: 'paid_manually', source: payment.source })
            }
            onToast?.({ type: 'success', message: 'Check uploaded — invoice marked Paid Manually' })
            setOpen(false)
        } catch (err) {
            const msg = err?.response?.data?.message || (uploadSucceeded
                ? 'Check uploaded, but failed to update status — retry from the menu.'
                : 'Upload failed')
            onToast?.({ type: uploadSucceeded ? 'error' : 'error', message: msg })
        } finally {
            setUploading(false)
            // Reset the input so re-picking the same file fires onChange again.
            if (fileInputRef.current) fileInputRef.current.value = ''
            // Always refetch on upload success — even if the PATCH failed,
            // the cheque image url is on the BE and the row needs to
            // reflect that (otherwise the menu still shows Upload Cheque).
            if (uploadSucceeded) onChanged?.()
        }
    }

    const generateLink = async (type) => {
        try {
            setBusy(true)
            const res = await api.post(`/cases/payments/${payment.id}/stripe-link`, { mediator_user_id: mediatorUserId, type, source: payment.source })
            const url = res?.data?.data?.url || res?.data?.data?.invoice_url || res?.data?.data?.payment_url
            if (url) window.open(url, '_blank')
            else onToast?.({ type: 'error', message: 'No URL returned from Stripe' })
            setOpen(false)
        } catch (err) {
            onToast?.({ type: 'error', message: 'Failed to generate Stripe link' })
        } finally {
            setBusy(false)
        }
    }

    return (
        <div className="relative inline-block">
            <button
                ref={buttonRef}
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
                disabled={busy}
                aria-label="Payment actions"
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="5" cy="12" r="2" />
                    <circle cx="12" cy="12" r="2" />
                    <circle cx="19" cy="12" r="2" />
                </svg>
            </button>
            {open && menuPos && ReactDOM.createPortal(
                <>
                    <div className="fixed inset-0 z-[9998]" onClick={() => setOpen(false)} />
                    <div
                        className="fixed w-56 bg-white border border-gray-200 rounded-md shadow-lg z-[9999] py-1"
                        style={{ top: menuPos.top, left: menuPos.left }}
                    >
                        {/* Scale parity (FinanceTable.jsx:399–427) — exactly
                            ONE option per status:
                              pending        → "Paid Manually"  (disabled w/ tooltip if no invoice)
                              paid           → "Pending"
                              paid_manually  → "Pending"
                            No "Mark as Paid by Stripe", no "Open/Generate
                            Payment Link", no "Generate Invoice Link" — Scale
                            doesn't surface those from this menu. */}
                        {status === 'pending' && (
                            // Strict null/undefined check matches scale-portal's
                            // hasInvoice semantic.  Previously this used `||`
                            // which incorrectly disabled the menu when
                            // invoice_id was 0 or "" (corrupt-empty-string).
                            (payment.invoiceId != null || payment.invoice_id != null) ? (
                                <button type="button" onClick={() => setStatus('paid_manually', 'Paid Manually')} disabled={busy} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50">
                                    Paid Manually
                                </button>
                            ) : (
                                <button type="button" disabled title="Invoice must be created before marking as paid manually" className="w-full text-left px-3 py-2 text-sm text-gray-400 cursor-not-allowed">
                                    Paid Manually
                                </button>
                            )
                        )}
                        {(status === 'paid' || status === 'paid_manually') && (
                            <button type="button" onClick={() => setStatus('pending', 'Pending')} disabled={busy} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50">
                                Pending
                            </button>
                        )}
                        {/* Upload Cheque — moved out of the table column.
                            Hidden file input is triggered by this menu item;
                            on successful upload the payment is auto-flipped
                            to paid_manually. Disabled once a cheque image
                            is already on file or the invoice is already
                            paid via Stripe. */}
                        {!chequeImageUrl && status !== 'paid' && (
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={busy || uploading}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
                            >
                                {uploading ? 'Uploading…' : 'Upload Check'}
                            </button>
                        )}
                        {/* View Cheque — only when a cheque image is on file
                            for this payment. Opens a lightweight preview
                            modal anchored to the body via the same portal. */}
                        {chequeImageUrl && (
                            <button
                                type="button"
                                onClick={() => { setOpen(false); setChequeOpen(true); }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                            >
                                View Check
                            </button>
                        )}
                    </div>
                </>,
                document.body,
            )}
            {/* File input sits outside the portal so it can stay mounted
                across menu open/close cycles. Hidden via display:none. */}
            <input
                ref={fileInputRef}
                type="file"
                accept={CHEQUE_ACCEPT_ATTR}
                style={{ display: 'none' }}
                onChange={(e) => handleChequeFile(e.target.files?.[0])}
            />
            {chequeOpen && chequeImageUrl && ReactDOM.createPortal(
                <div className="fixed inset-0 z-[10000] flex items-center justify-center" onClick={() => setChequeOpen(false)}>
                    <div className="absolute inset-0 bg-black/60" />
                    <div
                        className="relative bg-white rounded-lg shadow-xl max-w-[min(960px,92vw)] max-h-[92vh] flex flex-col overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                            <span className="text-sm font-semibold text-[#0e1e20]" style={{ fontFamily: "'Inter Tight', sans-serif" }}>
                                Check image
                            </span>
                            <button
                                type="button"
                                onClick={() => setChequeOpen(false)}
                                className="text-gray-400 hover:text-gray-700 p-1 rounded"
                                aria-label="Close"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        </div>
                        <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-gray-50 relative min-h-[200px]">
                            {/* Spinner — shown until the image's onLoad fires.
                                Absolutely positioned so the modal doesn't
                                jump in size when the image finally renders. */}
                            {!chequeLoaded && !chequeError && (
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <svg className="animate-spin" width="32" height="32" viewBox="0 0 24 24" fill="none" aria-label="Loading check image">
                                        <circle cx="12" cy="12" r="10" stroke="#e5e7eb" strokeWidth="3" />
                                        <path d="M22 12a10 10 0 0 0-10-10" stroke="#115e59" strokeWidth="3" strokeLinecap="round" />
                                    </svg>
                                </div>
                            )}
                            {chequeError ? (
                                <div className="text-sm text-gray-500 text-center px-6">
                                    Couldn't load the check image.<br />
                                    Try opening it in a new tab below.
                                </div>
                            ) : (
                                <img
                                    src={chequeImageUrl}
                                    alt="Check"
                                    className={`max-w-full max-h-[78vh] object-contain transition-opacity duration-150 ${chequeLoaded ? 'opacity-100' : 'opacity-0'}`}
                                    onLoad={() => setChequeLoaded(true)}
                                    onError={() => setChequeError(true)}
                                />
                            )}
                        </div>
                        <div className="px-4 py-3 border-t border-gray-200 flex justify-end">
                            <a
                                href={chequeImageUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs font-medium text-[#115e59] hover:underline"
                            >
                                Open in new tab ↗
                            </a>
                        </div>
                    </div>
                </div>,
                document.body,
            )}
        </div>
    )
}
